import { Chip, type ChipVariant } from '@/components/ui'
import type { SampleOrderShipment } from '../api/types'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function trackingChip(s: SampleOrderShipment): { label: string; variant: ChipVariant } {
  if (s.delivered_at) return { label: 'Delivered', variant: 'success' }
  const raw = (s.tracking_status ?? '').trim()
  if (!raw) return { label: s.tracking_number ? 'Shipped' : 'No tracking yet', variant: 'neutral' }
  const pretty = raw
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
  const variant: ChipVariant = /exception|fail|return/i.test(raw) ? 'danger' : 'info'
  return { label: pretty, variant }
}

/**
 * One row per Odoo delivery (a split order ships in several, each with its
 * own carrier / tracking / lot). Rows come from `sample_order_shipments`,
 * kept current by the reconciler, which keeps reading carrier status after
 * the order ships until every delivery is Delivered.
 */
export function ShipmentsTable({ shipments }: { shipments: SampleOrderShipment[] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border-subtle">
          <th className="text-role-table-header py-2 text-left">Delivery</th>
          <th className="text-role-table-header py-2 text-left">Items</th>
          <th className="text-role-table-header py-2 text-left">Carrier / tracking</th>
          <th className="text-role-table-header py-2 text-left">Status</th>
          <th className="text-role-table-header py-2 text-left">Lot / exp.</th>
          <th className="text-role-table-header py-2 text-left">Shipped</th>
        </tr>
      </thead>
      <tbody>
        {shipments.map((s) => {
          const chip = trackingChip(s)
          return (
            <tr key={s.id} className="border-b border-border-subtle align-top last:border-0">
              <td className="text-role-table-cell py-2">
                {s.odoo_picking_name ?? `Picking ${s.odoo_picking_id}`}
                {s.is_backorder ? (
                  <span className="ml-1.5 text-xs text-tertiary">(backorder)</span>
                ) : null}
              </td>
              <td className="text-role-table-cell py-2">
                {s.lines.length
                  ? s.lines
                      .map(
                        (l) =>
                          `${l.product_name ?? l.product_code ?? l.product_id} × ${Number(l.qty)}`,
                      )
                      .join(', ')
                  : '—'}
              </td>
              <td className="text-role-table-cell py-2">
                <div>{s.carrier_name ?? '—'}</div>
                {s.tracking_number ? (
                  s.tracking_link ? (
                    <a
                      href={s.tracking_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-link hover:underline"
                    >
                      {s.tracking_number}
                    </a>
                  ) : (
                    <span>{s.tracking_number}</span>
                  )
                ) : null}
              </td>
              <td className="text-role-table-cell py-2">
                <Chip size="xs" variant={chip.variant}>
                  {chip.label}
                </Chip>
                {s.delivered_at ? (
                  <div className="mt-0.5 text-xs text-tertiary">{formatDate(s.delivered_at)}</div>
                ) : null}
              </td>
              <td className="text-role-table-cell py-2">
                <div>{s.lot_number ?? '—'}</div>
                {s.expiration_date ? (
                  <div className="text-xs text-tertiary">exp. {formatDate(s.expiration_date)}</div>
                ) : null}
              </td>
              <td className="text-role-table-cell py-2">{formatDate(s.shipped_at)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
